import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WORKER_ROLES } from "@/lib/checklist";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2 } from "lucide-react";

export default function RoleSelectionDialog({ open }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!role) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/select-role", { worker_role: role });
      setUser(data);
      toast.success("Role selected", { description: `Welcome to your ${WORKER_ROLES.find((r) => r.key === role).label} checklist` });
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error("Could not save role", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-white sm:max-w-2xl"
        data-testid="role-selection-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Select your role</DialogTitle>
          <DialogDescription>
            Choose the role you&apos;re being onboarded for. This determines your compliance checklist.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2.5 sm:grid-cols-2">
          {WORKER_ROLES.map((r) => {
            const active = role === r.key;
            return (
              <button
                key={r.key}
                onClick={() => setRole(r.key)}
                data-testid={`role-option-${r.key}`}
                className={`text-left rounded-lg border p-4 transition ${
                  active
                    ? "border-teal-700 bg-teal-50 ring-2 ring-teal-200"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-heading font-semibold text-slate-900">{r.label}</div>
                  {active && <CheckCircle2 className="h-4 w-4 text-teal-700" />}
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">{r.description}</p>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!role || busy}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="role-continue-btn"
          >
            {busy ? "Saving…" : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
